import React, {useContext, useCallback, useEffect, useState, useMemo} from 'react';
import {useSwipeable} from 'react-swipeable';

import Modal from '~/src/components/modal/Modal';
import FeatureModal from '~/src/components/featureModal/FeatureModal';
import CommentReactionGraph from './graph/CommentReactionGraph';
import CommentReactionButton from './button/CommentReactionButton';

import {ReactionContext} from '~/src/components/context/ReactionContext';
import AppContext from '~/src/components/context/Context';
import {reactToComment, hasReaction} from '~/src/utilities/helpers/reaction';
import {Time} from '~/src/utilities/constants/time';
import {classNames} from '~/src/utilities/helpers/css';
import initPolyglot from '~/src/utilities/libs/polyglot';

import css from './CommentReaction.module.scss';
import i18n from './CommentReaction.i18n.json';

const getTranslation = initPolyglot(i18n);

const CommentReaction = ({commentId, reactions = {}}) => {
    const config = useContext(ReactionContext);

    const {reactions: userReactions} = useContext(AppContext);

    const [showDetails, setShowDetails] = useState(false);
    const [showLogin, setShowLogin] = useState(false);

    const triggerShowDetails = useCallback((event) => {
        event.preventDefault();
        setShowDetails(true);
    }, []);

    const triggerHideDetails = useCallback((event) => {
        event.preventDefault();
        setTimeout(() => {
            setShowDetails(false);
        }, Time.REACTION_CLOSE);
    });

    const onReactionClick = useCallback(
        (reaction) => {
            if (showDetails) {
                reactToComment(commentId, reaction);
            }
            if (window.innerWidth < parseInt(css.maxWidthTablet, 10)) {
                setTimeout(() => {
                    setShowDetails(false);
                }, Time.REACTION_CLOSE);
            }
        },
        [showDetails]
    );

    const closeReaction = useCallback(() => setShowDetails(false), []);
    const onLoginClose = useCallback(() => setShowLogin(false), []);

    useEffect(() => {
        if (showLogin) {
            return () => setShowDetails(false);
        }
    }, [showLogin]);

    const {hasReacted, userReaction, topReaction, total} = useMemo(() => {
        const hasReacted = hasReaction(commentId);
        const userReaction = hasReacted ? config.find((item) => hasReaction(commentId, item.key)) : null;
        const topReaction = config.reduce((top, item) => {
            const topCount = !top ? 0 : reactions[top.key] + (userReaction && top.key === userReaction.key ? 1 : 0);
            const itemCount = (reactions[item.key] || 0) + (userReaction && item.key === userReaction.key ? 1 : 0);

            return itemCount === 0 || topCount > itemCount ? top : item;
        }, null);
        const total = config.reduce((acc, item) => acc + (reactions[item.key] || 0), userReaction ? 1 : 0);

        return {
            hasReacted,
            userReaction,
            topReaction,
            total
        };
    }, [config, reactions, userReactions]);

    const totalCountStyles = userReaction ? {color: userReaction.colors.light} : null;

    const containerClasses = classNames(css, 'container', {showDetails, hasReacted});

    const handlers = useSwipeable({
        onSwipedUp: closeReaction,
        onSwipedDown: closeReaction
    });

    return (
        <div className={containerClasses} onMouseEnter={triggerShowDetails} onMouseLeave={triggerHideDetails}>
            {config.length > 0 && (
                <React.Fragment>
                    <div className={css.summary} onTouchEnd={triggerShowDetails}>
                        <div className={css.graphs}>
                            {config.map((item) => {
                                const hasReactedToComment = userReaction && userReaction.key === item.key;
                                const count = (reactions[item.key] || 0) + (hasReactedToComment ? 1 : 0);
                                const percentage = total > 0 ? (100 * count) / total : 100 / config.length;

                                return percentage > 0 ? <CommentReactionGraph {...item} key={item.key} count={count} percentage={percentage} /> : null;
                            })}
                        </div>

                        <span className={css.totalCount} style={totalCountStyles}>
                            {total}
                        </span>
                    </div>
                    <div className={css.topReaction}>
                        {topReaction && (
                            <span className={css.topReactionLabel} style={{color: topReaction.colors.light}}>
                                {topReaction.label}
                            </span>
                        )}
                        <span>
                            {getTranslation(userReaction && userReaction.key === topReaction.key ? 'topReactionReacted' : 'topReaction', {
                                smart_count: (topReaction && reactions[topReaction.key]) || 0
                            })}
                        </span>
                    </div>
                </React.Fragment>
            )}

            {showDetails && (
                <div {...handlers}>
                    <div className={css.details}>
                        <div className={css.detailsOverlay} onTouchEnd={triggerHideDetails} />
                        <div className={css.reactions}>
                            {config.map((item) => {
                                const hasReactedToComment = userReaction && userReaction.key === item.key;
                                const count = (reactions[item.key] || 0) + (hasReactedToComment ? 1 : 0);

                                return (
                                    <div key={item.key} className={css.reaction}>
                                        <CommentReactionButton
                                            {...item}
                                            reaction={item.key}
                                            count={count}
                                            onClick={onReactionClick}
                                            selected={hasReactedToComment}
                                        />

                                        <span className={css.reactionCount}>{count}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className={css.detailsLabel}>{getTranslation('detailsLabel')}</div>
                    </div>
                </div>
            )}

            {showLogin && (
                <Modal>
                    <FeatureModal onClose={onLoginClose} targetUri={window.location.href} />
                </Modal>
            )}
        </div>
    );
};

export default CommentReaction;
